import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cursor from 'legacy-src/utility/Cursor';
import { changeFullTextSearch } from 'legacy-src/utility/ItemListShared';
import SearchToolsLite from 'chameleon/ui-stack/ui/basic/search-tools/SearchToolsLite';
import SearchTipsButton from 'chameleon/ui-stack/ui/buttons/search-tips';
import SearchGuideModal from '../SearchGuideModal';
import { SearchGuideModalType } from '../SearchGuideModal/types';

/**
 * Full text search component to use for searching items with elastic search
 * https://academicmerit.atlassian.net/browse/FY-10310
 * */
const FullTextSearch = (props) => {
  const {
    className,
    component,
    inputContainerClassName,
    showTipsButton,
    tipsModalPage,
    inputPlaceholder = 'Search',
  } = props;
  const fullTextSearchCursor = cursor({}, [], (updatedFullTextSearch) => {
    changeFullTextSearch(component, updatedFullTextSearch);
  });

  /**
   * Component state and refs.
   * */
  const [searchTerm, setSearchTerm] = useState(props.searchTerm);
  const fullTextSearchInput = useRef(null);

  const changeFullTextSearchCursor = (text = null) => {
    fullTextSearchCursor.requestChange({
      searchTerm: text,
    });
  };
  const onInputClear = () => {
    setSearchTerm('');
    changeFullTextSearchCursor();
  };
  const onSearchClick = () => changeFullTextSearchCursor(searchTerm);
  const onInputKeyPress = ({ which, keyCode }) =>
    (which || keyCode) === 13 && onSearchClick();

  /**
   * Add `onFocus` and `onBlur` events for full text search input.
   * This is used to set `z-index` property of the parent in order to see the right border which
   * is hidden by the next right element.
   * */
  useEffect(() => {
    const onFullTextInputSearchFocus = () =>
      (fullTextSearchInput.current.parentElement.style.zIndex = 3);
    const onFullTextInputSearchBlur = () =>
      (fullTextSearchInput.current.parentElement.style.zIndex = 'auto');
    const onFullTextInputSearchMouseOut = () => {
      const focusedEl = document.activeElement;

      if (focusedEl !== fullTextSearchInput.current) {
        onFullTextInputSearchBlur();
      }
    };

    fullTextSearchInput.current.addEventListener(
      'focus',
      onFullTextInputSearchFocus,
    );
    fullTextSearchInput.current.addEventListener(
      'blur',
      onFullTextInputSearchBlur,
    );
    fullTextSearchInput.current.addEventListener(
      'mouseover',
      onFullTextInputSearchFocus,
    );
    fullTextSearchInput.current.addEventListener(
      'mouseout',
      onFullTextInputSearchMouseOut,
    );

    return () => {
      fullTextSearchInput.current?.removeEventListener(
        'focus',
        onFullTextInputSearchFocus,
      );
      fullTextSearchInput.current?.removeEventListener(
        'blur',
        onFullTextInputSearchBlur,
      );
      fullTextSearchInput.current?.removeEventListener(
        'mouseover',
        onFullTextInputSearchFocus,
      );
      fullTextSearchInput.current?.removeEventListener(
        'mouseout',
        onFullTextInputSearchMouseOut,
      );
    };
  });

  useEffect(() => {
    setSearchTerm(props.searchTerm || '');
  }, [props.searchTerm]);

  return (
    <div className={className}>
      <SearchToolsLite
        inputPlaceholder={inputPlaceholder}
        inputRef={fullTextSearchInput}
        inputContainerClassName={inputContainerClassName}
        inputClassName="rounded-24.8"
        text={searchTerm}
        onClearText={onInputClear}
        isSearchTextEmpty={!searchTerm}
        onInputKeyPress={onInputKeyPress}
        setText={setSearchTerm}
        searchTipsButton={
          showTipsButton ? (
            <SearchGuideModal
              page={tipsModalPage}
              button={<SearchTipsButton isIcon />}
            />
          ) : null
        }
      />
    </div>
  );
};

FullTextSearch.displayName = 'FullTextSearch';

FullTextSearch.propTypes = {
  className: PropTypes.string,
  inputContainerClassName: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  showTipsButton: PropTypes.bool,
  tipsModalPage: PropTypes.oneOf([
    SearchGuideModalType.Assessments,
    SearchGuideModalType.QuestionBank,
    SearchGuideModalType.StudentAssignments,
  ]),
};

FullTextSearch.defaultProps = {
  className: '',
  inputContainerClassName: 'w-128',
  showTipsButton: true,
  tipsModalPage: SearchGuideModalType.QuestionBank,
};

export default FullTextSearch;
